import React, { useState, useEffect } from 'react';
import type { TableComparisonFeature } from '@sy/ssr-types';
import { useMediaQuery } from '~/services/utils/hooks';
import { Translation } from '~/components/Translation';
import { RoundCheckmark, RoundCancel } from '~/components/CustomIcons';
import { CompetitorLogo } from '../CompetitorLogos';
import type { CompetitorType } from '../../types';
import {
	Columns,
	CompetitorColumn,
	CompetitorLogoText,
	CompetitorLogoWrapper,
	Container,
	FeaturesColumn,
	FeatureText,
	HeaderBackground,
	StreamYardColumn,
	StyledLogo,
	StyledText,
	Subtitle,
	TableCell,
	TableContainer,
	TableHeader,
	TableHeaderContainer,
	TextLogo,
	Title,
} from './TableComparison.styled';

interface TableComparisonProps {
	competitor: CompetitorType;
	fullCompetitorName?: string;
	titleKey?: string;
	subtitleKey?: string;
	features?: TableComparisonFeature[];
}

const TableComparison: React.FC<TableComparisonProps> = ({
	competitor,
	fullCompetitorName,
	titleKey,
	subtitleKey,
	features,
}) => {
	// Track mounting state to prevent hydration mismatches
	const [isMounted, setIsMounted] = useState(false);
	const isSmallTable = useMediaQuery('(max-width: 1080px)');
	const competitorLogoSize = isMounted && isSmallTable ? 20 : 30;

	useEffect(() => {
		setIsMounted(true);
	}, []);

	return (
		<Container>
			<TableHeaderContainer>
				{titleKey && (
					<Title variant="h2">
						<Translation tid={titleKey} />
					</Title>
				)}
				{subtitleKey && (
					<Subtitle variant="body">
						<Translation tid={subtitleKey} />
					</Subtitle>
				)}
			</TableHeaderContainer>
			{features && (
				<TableContainer>
					<Columns>
						<HeaderBackground />
						<FeaturesColumn>
							<TableHeader />
							{features.map((feature, index) => (
								<TableCell
									key={index}
									isLast={index === features.length - 1}
									isFeatureCell
								>
									<FeatureText variant="body">
										<Translation tid={feature.labelKey} />
									</FeatureText>
								</TableCell>
							))}
						</FeaturesColumn>
						<StreamYardColumn>
							<TableHeader isHighlighted>
								{(() => {
									if (!isMounted) {
										return <div style={{ height: '30px' }} />;
									}
									return isSmallTable ? (
										<TextLogo isInverted />
									) : (
										<StyledLogo isInverted />
									);
								})()}
							</TableHeader>
							{features.map((feature, index) => (
								<TableCell key={index} isLast={index === features.length - 1}>
									<RoundCheckmark size={isMounted && isSmallTable ? 16 : 28} />
									<StyledText variant="body2">
										<Translation tid={feature.streamYard.valueKey} />
									</StyledText>
								</TableCell>
							))}
						</StreamYardColumn>
						<CompetitorColumn>
							<TableHeader>
								<CompetitorLogoWrapper>
									{isMounted && !isSmallTable && (
										<CompetitorLogo
											competitor={competitor}
											size={competitorLogoSize}
										/>
									)}
									<CompetitorLogoText>
										{fullCompetitorName || competitor}
									</CompetitorLogoText>
								</CompetitorLogoWrapper>
							</TableHeader>
							{features.map((feature, index) => (
								<TableCell key={index} isLast={index === features.length - 1}>
									<RoundCancel size={isMounted && isSmallTable ? 16 : 28} />
									<StyledText variant="body2">
										<Translation tid={feature.competitor.valueKey} />
									</StyledText>
								</TableCell>
							))}
						</CompetitorColumn>
					</Columns>
				</TableContainer>
			)}
		</Container>
	);
};

export default TableComparison;
